import React, { MouseEvent } from 'react';
import { clsx } from 'clsx';
import { currentStepId, stepsId } from '../helper';
import styles from '../product-timeline.module.scss';
import ProductView from './product';
import StepView from './step';
import UrlActionView from './step-info/url-action';
import IconDownloadView from './icon-download';
import { Order } from '../interfaces/myOrder';
import { Product } from '../interfaces/orderSaleProduct';
import { Action, Timeline } from '../interfaces/timeline';
import { Icon, useAnalyticEvents } from '../fake';
import { useTranslation } from 'react-i18next';

const TimelineView: React.FC<{
  order: Order | null;
  product: Product;
  timeline: Timeline;
}> = ({ order, product, timeline }) => {
  const { sendAnalyticEvent } = useAnalyticEvents('orders_sales_timeline');

  const { t } = useTranslation();

  const { header, steps, printInvoiceUrl, actions = [] } = timeline;

  const stepsChronology = steps.stepsChronology;
  const stepList = Array.isArray(stepsChronology)
    ? stepsChronology
        .map((key) => {
          const step = steps[key];
          if (!step || Array.isArray(step)) {
            return null;
          }
          step.identifier = key;
          return step;
        })
        .filter((step) => step != null)
    : [];
  const lastStep = stepList[stepList.length - 1];

  const isSeller = timeline.kind === 'Seller';

  const analyticData = { label: isSeller ? 'seller' : 'buyer', product_id: product.productId };

  const callback = actions.find((a: Action) => a.name === 'callback');

  const handleCallback = (e: MouseEvent<HTMLElement>) => {
    if (callback != null && header != null) {
      const isDisabled = !callback.url;
      sendAnalyticEvent('timeline_callback', {
        ...analyticData,
        label: header.status || '',
        property: callback.messageTracking || null,
        value: isDisabled ? '0' : '1',
      });

      if (isDisabled) {
        e.preventDefault();
      }
    }
  };

  setTimeout(() => {
    const stepsIdE = document.getElementById(stepsId);
    const currentStepIdE = document.getElementById(currentStepId);
    if (typeof window !== 'undefined' && stepsIdE && currentStepIdE) {
      stepsIdE.scrollTop = currentStepIdE.offsetTop;
    } else if (typeof window !== 'undefined' && stepsIdE) {
      stepsIdE.scrollTop = stepsIdE.scrollHeight;
    }
  }, 1);

  return (
    <div className={styles.ordersSales__mainRight__timeline}>
      <ProductView
        product={product}
        isSeller={isSeller}
      />

      {header && header.message && (
        <div className={styles.ordersSales__mainRight__timeline__timeInfo}>
          <div className={clsx(styles.ordersSales__mainRight__timeline__timeInfo__label)}>{header.message}</div>
          {!!header.date && (
            <div className={styles.ordersSales__mainRight__timeline__timeInfo__value}>
              <span data-cy="timeline-header-date">{header.date}</span>
              {!!header.note && <b data-cy="timeline-header-note">&nbsp;-&nbsp;{header.note}</b>}
            </div>
          )}
        </div>
      )}

      <div
        className={styles.ordersSales__mainRight__steps}
        id={stepsId}
      >
        {stepList.map((step) => {
          return (
            <StepView
              key={step.identifier}
              order={order}
              product={product}
              step={step}
              timeline={timeline}
            />
          );
        })}
      </div>

      <div className={styles.ordersSales__mainRight__bottomButtons}>
        {lastStep && !!callback && (
          <UrlActionView
            disabled={!callback.url}
            url={callback.url}
            message={callback.message}
            className={clsx('vc-btn vc-btn--full vc-btn--outline', styles.ordersSales__mainRight__steps__step__btn)}
            newTab={false}
            onClick={handleCallback}
          >
            <div className={clsx(styles.ordersSales__mainRight__steps__step__box__actions__callbackBtn)}>
              <div className={styles.ordersSales__mainRight__steps__step__box__actions__callbackBtn_title}>
                <Icon
                  className={styles.ordersSales__mainRight__steps__step__box__actions__callbackBtn_icon}
                  name="phone"
                />
                <div>{callback.title}</div>
              </div>
              {!!callback.subtitle && (
                <div className={styles['ordersSales__mainRight__steps__step__box--text--625']}>{callback.subtitle}</div>
              )}
            </div>
          </UrlActionView>
        )}

        {lastStep && printInvoiceUrl && isSeller && (
          <a
            className={clsx('vc-btn vc-btn--full vc-btn--outline', styles.ordersSales__mainRight__steps__step__btn)}
            href={printInvoiceUrl}
            rel={'noreferrer'}
            target={'_blank'}
            onClick={() => {
              if (isSeller) {
                sendAnalyticEvent('timeline_b2c_seller_invoice', analyticData);
              }
            }}
          >
            <IconDownloadView />
            {isSeller ? t('TIMELINE.SELLER.DOWNLOAD_INVOICE') : t('MY_ACCOUNT.ORDERS_SALES.LIST.PRINT_INVOICE')}
          </a>
        )}
      </div>
    </div>
  );
};

export default TimelineView;
